import type { VehicleDetails } from "@/types/vehicleDetails";

/**
 * The shape `PriceSection` consumes.
 *
 * It is deliberately a SUPERSET-tolerant projection of `VehicleDetails`: every field is
 * carried over by spread in `toPriceSectionVehicleInfo`, so a field added to
 * `VehicleDetails` reaches the estimation payload without anyone having to remember a
 * second edit. The only divergences from `VehicleDetails` are `year` (string here, number
 * there - the form renders it) and the estimation-only `purchasePrice`.
 */
export interface PriceSectionVehicleInfo {
  vehicleType?: "car" | "mc";
  purchasePrice?: number;
  regNumber?: string;
  year?: string;
  make?: string;
  model?: string;
  engine?: string;
  fuelType?: string;
  registrationDate?: string;
  mileage?: number;
  // Enhanced fields for better estimation
  vin?: string;
  variant?: string;
  trim?: string;
  isNotImported?: string;
  origin?: string;
  bodyType?: string;
  transmission?: string;
  engineVolume?: string;
  electricDriveRange?: number;
  horsepower?: string;
  /** Dealer's CURRENT equipment selection (car.info prefill +/- dealer edits). */
  equipment?: string[];
}

/**
 * Build `PriceSection`'s `vehicleInfo` prop from form state.
 *
 * WHY THIS EXISTS: the post-an-ad widget used to hand-build this object as a 17-field
 * literal, which silently dropped trim, isNotImported, origin and equipment - the
 * estimation on that path was missing data the valuation path (which spreads the whole
 * `vehicleDetails`) has always sent. Building it by spread makes that class of bug
 * structurally impossible.
 */
export function toPriceSectionVehicleInfo(
  vehicleDetails: Partial<VehicleDetails> | undefined,
  overrides: { regNumber?: string; purchasePrice?: number } = {}
): PriceSectionVehicleInfo {
  const details = (vehicleDetails ?? {}) as Partial<VehicleDetails>;

  const info: PriceSectionVehicleInfo = {
    ...details,
    // `year` is a number in form state and a string in the estimation payload.
    year: details.year !== undefined && details.year !== null ? String(details.year) : undefined,
  };

  if (overrides.regNumber !== undefined) info.regNumber = overrides.regNumber;
  if (overrides.purchasePrice !== undefined) info.purchasePrice = overrides.purchasePrice;

  return info;
}
